'use client'
import { axiosAdmin } from '@/lib/axios'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

type PaginationResponse<T> = {
	data: T[]
	pagination: {
		page: number
		limit: number
		total: number
		hasNextPage: boolean
	}
}

export function useGetAllHeroBanners() {
	return useQuery({
		queryKey: ['getHeroBanners'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/hero-banners')
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetAllBrands() {
	return useQuery({
		queryKey: ['getBrands'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/brands')
				console.log(response?.data)
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetAllCategories(isPopular?: boolean) {
	return useQuery({
		queryKey: ['getCategories', isPopular],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/categories?isPopular=' + isPopular)
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetMostWantedInfinite(limit = 15) {
	return useInfiniteQuery<PaginationResponse<any>>({
		queryKey: ['mostWanted', limit],
		queryFn: async ({ pageParam = 1 }) => {
			const res = await axiosAdmin.get(
				`/user/products/most-wanted?page=${pageParam}&limit=${limit}`
			)
			return res.data
		},
		getNextPageParam: (lastPage) => {
			return lastPage.pagination.hasNextPage
				? lastPage.pagination.page + 1
				: undefined
		},
		initialPageParam: 1,
	})
}
export const useGetSimilarProducts = (productId: string, limit = 10) => {
	return useQuery({
		queryKey: ["similarProducts", productId, limit],
		queryFn: async () => {
			const res = await axiosAdmin.get(`user/product-cards/${productId}/similar?page=1&limit=${limit}`);
			return res.data.data || res.data.products;
		},
		enabled: !!productId,
	});
};

export function useGetSimilarProductsInfinite(productId: string, limit = 15) {
	return useInfiniteQuery<PaginationResponse<any>>({
		queryKey: ['similarProducts', productId, limit],
		queryFn: async ({ pageParam = 1 }) => {
			const res = await axiosAdmin.get(
				`/user/product-cards/${productId}/similar?page=${pageParam}&limit=${limit}`
			)
			// Fallback for old backend response to prevent breakage
			if (res.data.products) {
				return {
					data: res.data.products,
					pagination: { page: 1, limit: 100, total: res.data.products.length, hasNextPage: false }
				}
			}
			return res.data
		},
		getNextPageParam: (lastPage) => {
			return lastPage.pagination.hasNextPage
				? lastPage.pagination.page + 1
				: undefined
		},
		initialPageParam: 1,
		enabled: !!productId,
	})
}

export function useGetNewArrivalsInfinite(limit = 15) {
	return useInfiniteQuery<PaginationResponse<any>>({
		queryKey: ['newArrivals', limit],
		queryFn: async ({ pageParam = 1 }) => {
			const res = await axiosAdmin.get(
				`/user/products/new-arrivals?page=${pageParam}&limit=${limit}`
			)
			return res.data
		},
		getNextPageParam: (lastPage) => {
			return lastPage.pagination.hasNextPage
				? lastPage.pagination.page + 1
				: undefined
		},
		initialPageParam: 1,
	})
}

export function useGetAllPopularProducts() {
	return useQuery({
		queryKey: ['getPopularProducts'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/products/new-arrivals')
				console.log(response?.data)
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetAllBestSellers() {
	return useQuery({
		queryKey: ['getallBestSellers'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/products/best-sellers')
				console.log(response?.data)
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}


export const useGetOfferBanners = () =>
	useQuery({
		queryKey: ["offer-banners"],
		queryFn: async () => {
			const res = await axiosAdmin.get("/admin/offer-banners");
			return res.data;
		},
	});


export function useGetAllReviews() {
	return useQuery({
		queryKey: ['getallReviews'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/reviews')
				console.log(response?.data)
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetAllTestimonials() {
	return useQuery({
		queryKey: ['getallTestimonials'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/testimonials')
				console.log(response?.data)
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetAllFeatured() {
	return useQuery({
		queryKey: ['getfeatured'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('admin/product/featured')
				return response?.data?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetSingleCarousel(id: string) {
	return useQuery({
		queryKey: ['getcarousel', id],
		queryFn: async id => {
			const m = id.queryKey[1]
			try {
				if (m) {
					const response = await axiosAdmin.get(`/carousel/${m}`)
					return response?.data?.data
				}
				return null
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}
// export function useGetAdminsById(id: any) {
//   return useQuery({
//     queryKey: ['getAllAdmins', id],
//     // eslint-disable-next-line
//     queryFn: (id) => getAdminById(id)
//   });
// }
export function useGetAllOrders() {
	return useQuery({
		queryKey: ['getAllOrders'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/orders')
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetAllOrdersInfinite(limit = 10) {
	return useInfiniteQuery<PaginationResponse<any>>({
		queryKey: ['getAllOrdersInfinite', limit],
		queryFn: async ({ pageParam = 1 }) => {
			const res = await axiosAdmin.get(`user/orders?page=${pageParam}&limit=${limit}`)
			return {
				data: res.data.orders,
				pagination: res.data.pagination
			}
		},
		getNextPageParam: (lastPage) => {
			return lastPage.pagination?.hasNextPage
				? lastPage.pagination.page + 1
				: undefined
		},
		initialPageParam: 1,
	})
}

export function useGetAllAddresses() {
	return useQuery({
		queryKey: ['getAllAddresses'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/addresses')
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetAllOfferCards() {
	return useQuery({
		queryKey: ['getoffercards'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/offer-cards')
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetAllHighlights() {
	return useQuery({
		queryKey: ['getHighlights'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/highlights')
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}

export function useGetAllProductGallery() {
	return useQuery({
		queryKey: ['getProductGallery'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('user/photo-gallery')
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}




export function useGetAllPinCodes() {
	return useQuery({
		queryKey: ['getallpincodes'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get("/admin/pincode?status=active");
				console.log(response?.data);
				return response?.data;
			} catch (error) {
				console.log('err', error);
				throw error;
			}
		}
	});
}






export function useGetOfferCardById(id: any) {
	return useQuery({
		queryKey: ['getoffercard', id],
		queryFn: async () => {
			console.log(id)
			try {
				if (id) {
					const response = await axiosAdmin.get(`/user/offer-cards/${id}`);
					console.log(response?.data?.data);
					return response?.data;
				}
				return null
			} catch (error) {
				console.log('err', error);
				throw error;
			}
		}
	});
}

export function useGetOfferById(id: any) {
	return useQuery({
		queryKey: ['getoffercard', id],
		queryFn: async () => {
			console.log(id)
			try {
				if (id) {
					const response = await axiosAdmin.get(`/user/offers/${id}`);
					console.log(response?.data?.data);
					return response?.data;
				}
				return null
			} catch (error) {
				console.log('err', error);
				throw error;
			}
		}
	});
}

export function useGetProductCards() {
	return useQuery({
		queryKey: ['getProductCards'],
		queryFn: async () => {
			try {
				const response = await axiosAdmin.get('/user/product-cards')
				return response?.data
			} catch (error) {
				console.log('err', error)
				throw error
			}
		},
	})
}


// Most Wanted – Home preview
export function useGetMostWantedPreview(limit = 15) {
	return useQuery({
		queryKey: ["mostWantedPreview", limit],
		queryFn: async () => {
			const res = await axiosAdmin.get(
				`/user/products/most-wanted?page=1&limit=${limit}`
			);
			return res.data.data; // 👈 ONLY array
		},
	});
}

// New Arrivals – Home preview
export function useGetNewArrivalsPreview(limit = 15) {
	return useQuery({
		queryKey: ["newArrivalsPreview", limit],
		queryFn: async () => {
			const res = await axiosAdmin.get(
				`/user/products/new-arrivals?page=1&limit=${limit}`
			);
			return res.data.data;
		},
	});
}
